/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  findBy,
  rejectBy,
  without,
} from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

export default class AttendanceSlip extends Component {
  @service
  attendanceCodes;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  students;

  @service
  toaster;

  // mode determines whether it's a late arrival or early leaver.
  @tracked
  mode = 'arrival';

  // learner is the learner whose atendance is being updated.
  @tracked
  learner = null;

  // record is the learner's attendance record to update, if applicable.
  @tracked
  record = null;

  // date is the date object/record for the date in question.
  date = null;

  // period is the period record for the timetable period in question.
  period = null;

  // time is the time (HH:MM) at which the student arrived/left.
  @tracked
  time = '';

  @action
  setTime(value) {
    this.time = value;
  }

  // code is the selected attendance code (object).
  @tracked
  code = null;

  @action
  setCode(value) {
    this.code = value;
  }

  // notes is the user-entered explanation for the absence.
  @tracked
  notes = '';

  @action
  setNotes(value) {
    this.notes = value;
  }

  // dialogOpen determines whether the dialog is open.
  @tracked
  dialogOpen = false;

  // recordLoading flags whether there's an in-flight request for the attendance
  // record.
  @tracked
  recordLoading = false;

  // rememberDetails allows the user to retain the time, code and notes for the
  // next student.  Useful if a bus is late, for instance.
  @tracked
  rememberDetails = false;

  @action
  setRememberDetails(value) {
    this.rememberDetails = value;
  }

  // resetLearner flags whether the learner and attendance record should be
  // reset when the dialog is closed.
  resetLearner = true;

  // refresh allows the caller to pass in a function which refreshes attendance
  // records, which may be called to ensure that a newly-created record appears.
  refresh() {}

  // availableCodes generates the list of available attendance codes.
  @computed('attendanceCodes.{quickCodes.[],present}')
  get availableCodes() {
    return without(
      this.attendanceCodes.quickCodes,
      this.attendanceCodes.present,
    );
  }

  // availableNewCodes generates the list of new available attendance codes.
  @computed('attendanceCodes.{quickCodes.[],present.[]}')
  get availableNewCodes() {
    return rejectBy(
      without(this.attendanceCodes.quickCodes, this.attendanceCodes.present),
      'old',
    );
  }

  // presentCode provides a concise way to get the applicable present code.
  @alias('attendanceCodes.present.code')
  presentCode;

  @tracked
  saveInProgress;

  // title determines the title for the dialog and printed slip.
  get title() {
    switch (this.mode) {
      case 'arrival':
        return 'Late Arrival';
      case 'leaver':
        return 'Early Leaver';
    }
    return 'Attendance';
  }

  // buttonLabel determines the label for the default button.
  get buttonLabel() {
    switch (this.mode) {
      case 'arrival':
        return 'Process Arrival';
      case 'leaver':
        return 'Process Departure';
    }
    return 'Process Attendance';
  }

  // tooltip determines the tooltip for default button.
  get tooltip() {
    switch (this.mode) {
      case 'arrival':
        return 'Sign in a late or returning student';
      case 'leaver':
        return 'Sign out a student who is leaving early';
    }
    return '';
  }

  // icon determines the icon to show in the button.
  get icon() {
    switch (this.mode) {
      case 'arrival':
        return 'update';
      case 'leaver':
        return 'restore';
    }
    return 'schedule';
  }

  // dateFormatted formats the date in a human-friendly way.
  @computed('date.date')
  get dateFormatted() {
    return moment(this.date.date, 'YYYY-MM-DD').format('D MMM YYYY');
  }

  // minMoment determines the earliest allowable time as a Moment.
  @computed('period.startTime', 'record.movements.[]')
  get minMoment() {
    if (isEmpty(this.record)) {
      return moment(
        `${this.get('date.date')}T${this.get('period.startTime')}`,
        'YYYY-MM-DDTHH:mm',
      );
    }
    return moment.unix(this.get('record.movements.lastObject.time'));
  }

  // maxMoment determines the latest allowable time as a Moment.
  @computed('period.endTime')
  get maxMoment() {
    return moment(
      `${this.get('date.date')}T${this.get('period.endTime')}`,
      'YYYY-MM-DDTHH:mm',
    );
  }

  // timeValidations provides the validation rules for the time input.
  @computed('mode', 'record.date', 'minMoment', 'maxMoment')
  get timeValidations() {
    let minTime = this.minMoment.format('h:mm A');
    let maxTime = this.maxMoment.format('h:mm A');
    let minMessage = `Time must be after the start of the ${this.strings.timetablePeriod} (${minTime}).`;
    if (this.minMoment.format('HH:mm') !== this.get('period.startTime')) {
      // Working with an existing absence - not the start of the period.
      if (this.mode === 'arrival') {
        minMessage = `Time must be after ${this.get(
          'learner.firstName',
        )}'s absence began (${minTime})`;
      } else {
        minMessage = `Time must be after ${this.get(
          'learner.firstName',
        )} arrived (${minTime})`;
      }
    }
    return [
      {
        message: minMessage,
        validate: (val) => {
          if (isEmpty(val)) {
            return true;
          }
          let parsed = moment(
            `${this.get('date.date')}T${val}`,
            'YYYY-MM-DDTHH:mm',
          );
          return parsed.isValid() && parsed.isSameOrAfter(this.minMoment, 'm');
        },
      },
      {
        message: `Time must be before the end of the ${this.strings.timetablePeriod} (${maxTime}).`,
        validate: (val) => {
          if (isEmpty(val)) {
            return true;
          }
          let parsed = moment(
            `${this.get('date.date')}T${val}`,
            'YYYY-MM-DDTHH:mm',
          );
          return parsed.isValid() && parsed.isSameOrBefore(this.maxMoment, 'm');
        },
      },
    ];
  }

  // isInvalidLearner determines the validity of the learner in this mode.
  get isInvalidLearner() {
    // A learner is always required.
    if (isEmpty(this.learner)) {
      return true;
    }

    switch (this.mode) {
      case 'leaver':
        return this.isInvalidLeaver;
      case 'arrival':
        return this.isInvalidArrival;
    }

    // Unknown mode - don't pretend it's valid.
    return true;
  }

  // isInvalidArrival determines the validity of an arrival.
  @computed('mode', 'learner', 'presentCode', 'record', 'recordLoading')
  get isInvalidArrival() {
    if (this.mode !== 'arrival') {
      // Not an arrival, so can't be an invalid arrival.
      return false;
    }

    if (this.recordLoading) {
      // Record is loading - so we're not sure yet.  Don't show an error.
      return false;
    }

    if (isEmpty(this.record) || this.record.code !== this.presentCode) {
      // Record is empty, or is not currently marked as present.  We're good.
      return false;
    }

    // Record isn't loading, and is currently marked as present.  Can't arrive
    // if you're already here.
    return true;
  }

  // isInvalidLeaver determines the validity of a leaver.
  @computed(
    'mode',
    'learner',
    'presentCode',
    'record',
    'recordLoading',
    'attendanceCodes.bulkOffsite.[]',
  )
  get isInvalidLeaver() {
    if (this.mode !== 'leaver') {
      // Not a leaver, so can't be an invalid leaver.
      return false;
    }

    if (this.recordLoading) {
      // Record is loading - so we're not sure yet.  Don't show an error.
      return false;
    }

    if (isEmpty(this.record)) {
      // No record; can't leave if you're not here.
      return true;
    }
    if (
      this.record.code !== this.presentCode &&
      isEmpty(
        findBy(this.attendanceCodes.bulkOffsite, 'code', this.record.code),
      )
    ) {
      // Not currently marked as present (or offsite).
      // Can't leave if you're not here(ish).
      return true;
    }

    // Non-empty record that's currently marked present.  We're good.
    return false;
  }

  // timestamp converts the selected time to a unix timestamp.
  @computed('time', 'date.date')
  get timestamp() {
    return moment(`${this.date.date}T${this.time}`, 'YYYY-MM-DDTHH:mm').unix();
  }

  // printStyles generates the base (static) styles for printing slips.
  printStyles(landscape) {
    return `
      <style type="text/css">
        body {
          font-family: -apple-system, BlinkMacSystemFont, Inter, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
        }

        .learner-details.layout-column {
          border: 1px dotted #000;
          padding: 4px;
          align-items: stretch;
        }
        .signature-panel {
          height: 48px;
          width: 100%;
          border-bottom: 1px dotted #000;
        }

        h3, h4 {
          margin-bottom: 0;
        }
        h4 {
          margin-top: 0;
        }
        p {
          margin-top: 4px;
        }

        .layout-row {
          display: flex;
          align-items: flex-start;
          flex-wrap: wrap;
        }
        .layout-column {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .layout-align-start-center {
          align-items: center;
        }
        .layout-align-center {
          justify-content: center;
        }

        ${landscape ? `@media print{@page {size: landscape}}` : ``}
      </style>
    `;
  }

  // processArrival processes a late arrival.
  processArrival() {
    if (isEmpty(this.record)) {
      // No attendance record yet - we'll have to create one.
      let record = this.store.createRecord('attendance/record', {
        schoolId: this.get('session.school'),
        personId: this.learner,
        authorId: this.get('session.person'),
        code: this.presentCode,
        notes: '',
        dateId: this.date,
        date: this.date.date,
        periodId: this.period,
      });

      let time = moment(
        `${this.date.date}T${this.period.startTime}`,
        'YYYY-MM-DDTHH:mm',
      ).unix();
      record.set('movements', [
        {
          time,
          code: this.code.code,
          authorId: this.get('session.person.id'),
          notes: this.notes,
        },
        {
          time: this.timestamp,
          code: this.presentCode,
          authorId: this.get('session.person.id'),
        },
      ]);

      return record.save().then(() => {
        // As we've created a new record, we should trigger a refresh to ensure
        // that it gets displayed.  This probably isn't a promise, so there's no
        // need to return it.
        this.refresh();
      });
    } else {
      // Adjust the code on the latest movement, as long as it's an absence.
      let lastMovement = this.get('record.movements.lastObject');
      if (isEmpty(lastMovement)) {
        // No movements - we shouldn't be here, but we can add one.
        let time = moment(
          `${this.date}T${this.period.startTime}`,
          'YYYY-MM-DDTHH:mm',
        ).unix();
        this.record.movements.createFragment({
          time,
          code: this.code.code,
          authorId: this.get('session.person.id'),
          notes: this.notes,
        });
      } else {
        lastMovement.setProperties({
          code: this.code.code,
          authorId: this.get('session.person.id'),
          notes: this.notes,
        });
      }

      // Persist the change, as the back end will only allow one new or updated
      // Movement at a time.
      this.record.setProperties({
        authorId: this.get('session.person'),
        code: this.code.code,
        notes: this.notes,
        reason: this.notes,
        effectiveFrom: lastMovement.time,
      });
      return this.record.save().then((record) => {
        // Add a Movement to make the student become present.
        record.movements.createFragment({
          time: this.timestamp,
          code: this.presentCode,
          authorId: this.get('session.person.id'),
        });

        // Update base record properties.
        record.setProperties({
          authorId: this.get('session.person'),
          code: this.presentCode,
          notes: '',
          reason: this.notes,
          effectiveFrom: this.timestamp,
        });

        return record.save();
      });
    }
  }

  // processLeaver processes an early leaver.
  processLeaver() {
    this.record.movements.createFragment({
      time: this.timestamp,
      code: this.code.code,
      // authorId on a Movement is just the ID (no relationship).
      authorId: this.get('session.person.id'),
      notes: this.notes,
    });
    this.record.setProperties({
      code: this.code.code,
      // authorId on the Record is a relationship.
      authorId: this.get('session.person'),
      notes: this.notes,
      reason: this.notes,
      effectiveFrom: this.timestamp,
    });
    return this.record.save();
  }

  // save processes/saves the attendance record, optionally printing a slip.
  @action
  save({ print }) {
    this.saveInProgress = true;
    let promise = Promise.resolve();
    switch (this.mode) {
      case 'arrival':
        promise = this.processArrival();
        break;
      case 'leaver':
        promise = this.processLeaver();
        break;
    }
    promise
      .then(() => {
        if (print) {
          // Close the dialog immediately, as we don't need it any more - but
          // don't use closeDialog() as we don't want to reset the learner etc.
          this.dialogOpen = false;
          // Printing calls closeDialog() when done, cleaning things up.
          this.print();
        } else {
          // If not printing, just close/reset the dialog.
          this.closeDialog();
        }
      })
      .catch(() => {
        this.toaster.error(
          'Failed to save attendance record.  Please refresh and try again.',
        );
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  // print prints an early/late slip.
  print() {
    let styles = this.printStyles(false);
    let printHTML = this.element.querySelector('div.slip').innerHTML;

    let printer = this.element.querySelector('iframe.slip-printer-frame');
    printer.contentDocument.title = `${this.title} Slip}`;
    printer.contentDocument.body.innerHTML = styles + printHTML;

    // Trigger the actual printing ~500ms after content insertion.
    later(printer.contentWindow, 'print', 500);

    // Delay slightly longer before closing and resetting, to avoid a
    // race condition which sometimes closes the dialog without printing.
    later(this, 'closeDialog', 1000);
  }

  // openDialog opens the dialog, resetting some properties if applicable.
  openDialog() {
    if (this.rememberDetails) {
      // Just open the dialog, as we're remembering the absence details.
      this.dialogOpen = true;
    } else {
      // Reset the code, notes and time for a fresh start.
      let { code, notes } = this.getDefaultDetails();
      this.code = code;
      this.notes = notes;
      this.time = moment().format('HH:mm');
      this.dialogOpen = true;
    }
  }

  // getDefaultDetails gets the default code and notes from the attendance
  // record, if applicable.
  getDefaultDetails() {
    let code = null;
    let notes = '';
    if (!isEmpty(this.record) && this.mode === 'arrival') {
      // If we have an existing record, default to the existing/current code.
      let latestCode = this.record.get('movements.lastObject.code');
      if (latestCode !== this.get('attendanceCodes.unknown.code')) {
        code = findBy(this.availableCodes, 'code', latestCode);
        notes = this.record.get('movements.lastObject.notes');
      }
    }
    return { code, notes };
  }

  // closeDialog closes the dialog and resets some relevant properties.
  @action
  closeDialog() {
    let props = {
      dialogOpen: false,
      saveInProgress: false,
    };
    if (this.resetLearner) {
      props.learner = null;
      props.record = null;
    }
    this.setProperties(props);
  }

  @action
  handleOpenDialog() {
    this.openDialog();
  }

  @action
  openWithLearner(learner, record) {
    this.learner = learner;
    this.record = record;
    this.openDialog();
  }

  @action
  updateTime() {
    this.time = moment().format('HH:mm');
  }

  @action
  setLearner(learner) {
    this.learner = learner;
    this.recordLoading = true;
    this.store
      .query('attendance/record', {
        dateId: this.date.id,
        periodId: this.period.id,
        personId: learner.id,
      })
      .then((records) => {
        if (!isEmpty(records) && !isEmpty(records[0])) {
          this.record = records[0];
          if (!this.rememberDetails) {
            let defaults = this.getDefaultDetails();
            if (!isEmpty(defaults.code)) {
              this.setProperties(defaults);
            }
          }
        }
      })
      .finally(() => {
        this.recordLoading = false;
      });
  }

  @action
  saveAndPrint() {
    this.save({ print: true });
  }
}
